import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatEdgeForDisplay, conflictKind, type EdgeForDisplay } from "@/components/research-hub/labels";

export default function ResearchKGHealth() {
  const qc = useQueryClient();
  const [threshold, setThreshold] = useState(0.9);

  const nodeCounts = useQuery({
    queryKey: ["kg_node_counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_kg_nodes")
        .select("node_type")
        .limit(10000);
      if (error) throw error;
      const m = new Map<string, number>();
      (data ?? []).forEach((r: any) => m.set(r.node_type, (m.get(r.node_type) ?? 0) + 1));
      return [...m.entries()].sort((a, b) => b[1] - a[1]);
    },
  });

  const edgeCounts = useQuery({
    queryKey: ["kg_edge_counts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_kg_edges")
        .select("edge_type,extractor,confidence")
        .limit(20000);
      if (error) throw error;
      const byType = new Map<string, number>();
      const byExtractor = new Map<string, { count: number; conf: number }>();
      (data ?? []).forEach((r: any) => {
        byType.set(r.edge_type, (byType.get(r.edge_type) ?? 0) + 1);
        const k = r.extractor ?? "unknown";
        const cur = byExtractor.get(k) ?? { count: 0, conf: 0 };
        cur.count += 1;
        cur.conf += Number(r.confidence ?? 0);
        byExtractor.set(k, cur);
      });
      return {
        byType: [...byType.entries()].sort((a, b) => b[1] - a[1]),
        byExtractor: [...byExtractor.entries()]
          .map(([k, v]) => ({ extractor: k, count: v.count, avgConfidence: v.conf / v.count }))
          .sort((a, b) => b.count - a.count),
      };
    },
  });

  const orphans = useQuery({
    queryKey: ["kg_orphans"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kg_orphan_nodes", { max_rows: 200 });
      if (error) throw error;
      return data as Array<{ id: string; label: string; node_type: string; created_at: string }>;
    },
  });

  const duplicates = useQuery({
    queryKey: ["kg_duplicates", threshold],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("kg_duplicate_candidates", {
        threshold,
        max_rows: 100,
      });
      if (error) throw error;
      return data as Array<{
        node_a: string;
        node_b: string;
        label_a: string;
        label_b: string;
        node_type: string;
        similarity: number;
      }>;
    },
  });

  const conflicts = useQuery({
    queryKey: ["kg_conflicts_open"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_kg_conflicts")
        .select("*")
        .eq("status", "open")
        .order("created_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data;
    },
  });

  // Resolve the conflicting edge ids to human labels in ONE call so each conflict
  // card reads "Energy → Inflation (supports)" instead of "A: abc123de ↔ B: xyz789ab".
  const conflictEdgeIds = Array.from(
    new Set(
      (conflicts.data ?? [])
        .flatMap((c: any) => [c.edge_id_a, c.edge_id_b])
        .filter(Boolean) as string[],
    ),
  );
  const edgeLabels = useQuery({
    queryKey: ["kg_edges_with_labels", conflictEdgeIds],
    enabled: conflictEdgeIds.length > 0,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)("kg_edges_with_labels", {
        _edge_ids: conflictEdgeIds,
      });
      if (error) throw error;
      const map = new Map<string, EdgeForDisplay>();
      ((data ?? []) as EdgeForDisplay[]).forEach((e) => {
        if (e.edge_id) map.set(e.edge_id, e);
      });
      return map;
    },
  });
  const edgeLabelMap = edgeLabels.data ?? new Map<string, EdgeForDisplay>();

  const mergeMutation = useMutation({
    mutationFn: async ({ source, target }: { source: string; target: string }) => {
      const { error } = await supabase.rpc("kg_merge_nodes", {
        source_id: source,
        target_id: target,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Nodes merged");
      qc.invalidateQueries({ queryKey: ["kg_duplicates"] });
      qc.invalidateQueries({ queryKey: ["kg_orphans"] });
      qc.invalidateQueries({ queryKey: ["kg_node_counts"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const deleteOrphan = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("research_kg_nodes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Node deleted");
      qc.invalidateQueries({ queryKey: ["kg_orphans"] });
      qc.invalidateQueries({ queryKey: ["kg_node_counts"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const resolveConflict = useMutation({
    mutationFn: async ({ id, resolution }: { id: string; resolution: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("research_kg_conflicts")
        .update({
          status: "resolved",
          resolution,
          resolved_at: new Date().toISOString(),
          resolved_by: u?.user?.id ?? null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Conflict resolved");
      qc.invalidateQueries({ queryKey: ["kg_conflicts_open"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const totalNodes = (nodeCounts.data ?? []).reduce((s, [, c]) => s + c, 0);
  const totalEdges = (edgeCounts.data?.byType ?? []).reduce((s, [, c]) => s + c, 0);

  return (
    <div className="space-y-6">
      {/* Top metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard label="Total nodes" value={totalNodes} />
        <MetricCard label="Total edges" value={totalEdges} />
        <MetricCard label="Orphan nodes" value={orphans.data?.length ?? 0} />
        <MetricCard label="Open conflicts" value={conflicts.data?.length ?? 0} />
      </div>

      {/* Counts by type */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Nodes by type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {(nodeCounts.data ?? []).map(([t, c]) => (
              <div key={t} className="flex justify-between text-sm">
                <Badge variant="outline">{t}</Badge>
                <span className="font-mono">{c}</span>
              </div>
            ))}
            {!nodeCounts.data?.length && <p className="text-xs text-muted-foreground">No nodes yet.</p>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Edges by type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {(edgeCounts.data?.byType ?? []).map(([t, c]) => (
              <div key={t} className="flex justify-between text-sm">
                <Badge variant="secondary">{t}</Badge>
                <span className="font-mono">{c}</span>
              </div>
            ))}
            {!edgeCounts.data?.byType.length && <p className="text-xs text-muted-foreground">No edges yet.</p>}
          </CardContent>
        </Card>
      </div>

      {/* Extractor stats */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Extractor mix</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Extractor</TableHead>
                <TableHead className="text-right">Edges</TableHead>
                <TableHead className="text-right">Avg confidence</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(edgeCounts.data?.byExtractor ?? []).map((r) => (
                <TableRow key={r.extractor}>
                  <TableCell className="font-mono text-xs">{r.extractor}</TableCell>
                  <TableCell className="text-right font-mono">{r.count}</TableCell>
                  <TableCell className="text-right font-mono">
                    {r.avgConfidence.toFixed(3)}
                  </TableCell>
                </TableRow>
              ))}
              {!edgeCounts.data?.byExtractor.length && (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-xs text-muted-foreground">
                    No edges yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Conflicts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Open conflicts ({conflicts.data?.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {!conflicts.data?.length ? (
            <p className="text-xs text-muted-foreground">No open conflicts.</p>
          ) : (
            <div className="space-y-3">
              {conflicts.data.map((c: any) => {
                const edgeA = c.edge_id_a ? edgeLabelMap.get(c.edge_id_a) : undefined;
                const edgeB = c.edge_id_b ? edgeLabelMap.get(c.edge_id_b) : undefined;
                return (
                <div key={c.id} className="border rounded-md p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs">
                      <Badge variant="destructive">{conflictKind(c.kind)}</Badge>
                      <span className="text-muted-foreground">
                        {format(new Date(c.created_at), "dd/MM/yyyy HH:mm")}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => resolveConflict.mutate({ id: c.id, resolution: "accept_a" })}>
                        Keep A
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => resolveConflict.mutate({ id: c.id, resolution: "accept_b" })}>
                        Keep B
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => resolveConflict.mutate({ id: c.id, resolution: "ignored" })}>
                        Ignore
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-muted/40 p-2 rounded">
                      <span className="font-semibold text-muted-foreground">Keep A: </span>
                      {edgeLabels.isLoading && !edgeA
                        ? "resolving…"
                        : formatEdgeForDisplay(edgeA ?? { from_id: c.edge_id_a })}
                    </div>
                    <div className="bg-muted/40 p-2 rounded">
                      <span className="font-semibold text-muted-foreground">Keep B: </span>
                      {edgeLabels.isLoading && !edgeB
                        ? "resolving…"
                        : formatEdgeForDisplay(edgeB ?? { from_id: c.edge_id_b })}
                    </div>
                  </div>
                  {c.proposed_payload && (
                    <pre className="text-xs bg-muted/30 p-2 rounded overflow-x-auto">
                      {JSON.stringify(c.proposed_payload, null, 2)}
                    </pre>
                  )}
                  {c.notes && <p className="text-xs text-muted-foreground">{c.notes}</p>}
                </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Duplicates */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Duplicate candidates</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">Similarity ≥</span>
            <div className="w-48">
              <Slider
                value={[threshold]}
                min={0.7}
                max={0.99}
                step={0.01}
                onValueChange={(v) => setThreshold(v[0])}
              />
            </div>
            <span className="text-xs font-mono">{threshold.toFixed(2)}</span>
            <span className="text-xs text-muted-foreground ml-auto">
              {duplicates.data?.length ?? 0} pairs
            </span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Node A</TableHead>
                <TableHead>Node B</TableHead>
                <TableHead className="text-right">Similarity</TableHead>
                <TableHead className="text-right">Merge</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(duplicates.data ?? []).map((d) => (
                <TableRow key={`${d.node_a}-${d.node_b}`}>
                  <TableCell>
                    <Badge variant="outline">{d.node_type}</Badge>
                  </TableCell>
                  <TableCell>
                    <Link className="hover:underline" to={`/partner/research/kg/${d.node_a}`}>
                      {d.label_a}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link className="hover:underline" to={`/partner/research/kg/${d.node_b}`}>
                      {d.label_b}
                    </Link>
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {d.similarity.toFixed(3)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => mergeMutation.mutate({ source: d.node_a, target: d.node_b })}
                      >
                        A→B
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => mergeMutation.mutate({ source: d.node_b, target: d.node_a })}
                      >
                        B→A
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!duplicates.data?.length && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-xs text-muted-foreground">
                    No duplicate candidates above this threshold.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Orphans */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Orphan nodes ({orphans.data?.length ?? 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Label</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(orphans.data ?? []).map((n) => (
                <TableRow key={n.id}>
                  <TableCell>
                    <Link className="hover:underline" to={`/partner/research/kg/${n.id}`}>
                      {n.label}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{n.node_type}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {format(new Date(n.created_at), "dd/MM/yyyy")}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm(`Delete orphan node "${n.label}"?`)) deleteOrphan.mutate(n.id);
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!orphans.data?.length && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-xs text-muted-foreground">
                    No orphan nodes.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-2xl font-bold mt-1">{value.toLocaleString()}</div>
      </CardContent>
    </Card>
  );
}
